import {
  createStore,
  emitOnce,
  select,
  setProp,
  setProps,
  withProps,
} from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

import { IManufacturer } from './manufacturer.model';

export interface ManufacturerProps {
  ui: {
    searchTerm: string | null;
    letter: string | null;
  };
  manufacturers: IManufacturer[];
  selectedManufacturers: IManufacturer[];
  selectedManufacturersIDs: number[];
  loading: boolean;
  error: boolean;
}

const initialProps: ManufacturerProps = {
  ui: {
    searchTerm: null,
    letter: null,
  },
  manufacturers: [],
  selectedManufacturers: [],
  selectedManufacturersIDs: [],
  loading: false,
  error: false,
};

const STORE_NAME = 'sandoz-v2-manufacturers';

@Injectable({ providedIn: 'root' })
export class ManufacturerRepository {
  private readonly store;

  public readonly searchTerm$: Observable<string | null>;
  public readonly letter$: Observable<string | null>;
  public readonly ui$;
  public readonly selectedManufacturers$: Observable<IManufacturer[]>;
  public readonly manufacturers$: Observable<IManufacturer[]>;

  public readonly selectedManufacturersIDs$: Observable<number[]>;
  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<boolean>;

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<ManufacturerProps>(initialProps),
    );

    this.searchTerm$ = this.store.pipe(
      select(state => state.ui.searchTerm),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.letter$ = this.store.pipe(
      select(state => state.ui.letter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedManufacturers$ = this.store.pipe(
      select(state => state.selectedManufacturers),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.manufacturers$ = this.store.pipe(
      select(state => state.manufacturers),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.selectedManufacturersIDs$ = this.store.pipe(
      select(state => state.selectedManufacturersIDs),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.ui$ = this.store.pipe(
      select(state => state.ui),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getStoreProp<K extends keyof ManufacturerProps>(
    key: K,
  ): ManufacturerProps[K] {
    const value = this.store.getValue()[key];
    if (key === 'selectedManufacturersIDs' && Array.isArray(value)) {
      return value.flat() as ManufacturerProps[K];
    }

    return value;
  }

  setStoreProp<
    K extends keyof ManufacturerProps,
    V extends ManufacturerProps[K],
  >(key: K, value: V): void {
    this.store.update(setProp(key, value));
  }

  getSelectedManufacturersIds(): number[] {
    const selectedManufacturers =
      this.store.getValue()['selectedManufacturers'];

    return selectedManufacturers.map(m => m.id).flat();
  }

  setSearchTerm(searchTerm: string): void {
    this.store.update(setProps({ ui: { letter: '', searchTerm: searchTerm } }));
  }

  setLetter(letter: string): void {
    this.store.update(setProps({ ui: { letter: letter, searchTerm: '' } }));
  }

  clearSelection(): void {
    emitOnce(() => {
      this.setStoreProp('selectedManufacturers', []);
      this.setStoreProp('selectedManufacturersIDs', []);
    });
  }

  resetStore(): void {
    emitOnce(() => {
      this.store.reset();
      this.setStoreProp('selectedManufacturers', []);
      this.setStoreProp('selectedManufacturersIDs', []);
    });
  }
}
